import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useUserStore } from '../lib/userStore';
import { useLanguageStore } from '../lib/languageStore';
import { translate } from '../lib/translations';
import toast from 'react-hot-toast';
import { Calendar } from 'lucide-react';

interface BirthdateModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function BirthdateModal({ isOpen, onComplete }: BirthdateModalProps) {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [loading, setLoading] = useState(false);
  const userId = useUserStore((state) => state.user_id);
  const setUserDateOfBirth = useUserStore((state) => state.setDateOfBirth);
  const { language } = useLanguageStore();

  // Mode arsenal : ne jamais afficher ce modal
  const ARSENAL_MODE = (import.meta as any).env?.VITE_ARSENAL_MODE === 'true';

  if (ARSENAL_MODE || !isOpen) return null;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);
  const months = language === 'fr' ? MONTHS_FR : MONTHS_EN;

  // Nombre de jours correct selon le mois/année sélectionnés
  const daysInMonth = month
    ? new Date(year ? parseInt(year) : 2000, parseInt(month), 0).getDate()
    : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const isComplete = day && month && year;

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth);
    // Réinitialiser le jour si hors plage du nouveau mois
    if (day) {
      const maxDays = newMonth
        ? new Date(year ? parseInt(year) : 2000, parseInt(newMonth), 0).getDate()
        : 31;
      if (parseInt(day) > maxDays) setDay('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      toast.error(translate('error.user.not.found', language));
      return;
    }

    if (!isComplete) {
      toast.error(translate('date.of.birth.required', language));
      return;
    }

    // Valider que la date n'est pas dans le futur
    const selectedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (selectedDate > new Date()) {
      toast.error(language === 'fr' ? 'La date ne peut pas être dans le futur' : 'Date cannot be in the future');
      return;
    }

    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('update_user_birthdate', {
        p_user_id: userId,
        p_date_of_birth: dateString,
      });

      if (error) throw error;

      // Vérifier si la réponse indique une erreur
      if (typeof data === 'string' && data.toLowerCase().includes('erreur')) {
        toast.error(data);
        return;
      }

      // Mettre à jour le store avec la nouvelle date de naissance
      setUserDateOfBirth(dateString);

      toast.success(translate('date.of.birth.updated', language));

      // Fermer le modal et rafraîchir
      onComplete();

      // Petit délai pour laisser le toast apparaître avant le rafraîchissement
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Error updating birthdate:', error);
      toast.error(error.message || translate('error.updating.birthdate', language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex={-1}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <div className="w-100 text-center">
              <Calendar className="text-primary mx-auto mb-3" size={48} />
              <h5 className="modal-title">{translate('complete.your.profile', language)}</h5>
            </div>
          </div>

          <div className="modal-body pt-2">
            <p className="text-center mb-4">
              {translate('birthdate.request.message', language)}
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  {translate('date.of.birth', language)} *
                </label>

                {/* Trois menus déroulants séparés — bien visibles sur mobile */}
                <div className="row g-2">
                  {/* Jour */}
                  <div className="col-4">
                    <label className="form-label small text-muted mb-1 d-block text-center">
                      {language === 'fr' ? 'Jour' : 'Day'}
                    </label>
                    <select
                      className="form-select form-select-lg text-center"
                      value={day}
                      onChange={(e) => setDay(e.target.value)}
                      required
                      style={{ fontSize: '1.1rem' }}
                    >
                      <option value="">--</option>
                      {days.map((d) => (
                        <option key={d} value={String(d)}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Mois */}
                  <div className="col-4">
                    <label className="form-label small text-muted mb-1 d-block text-center">
                      {language === 'fr' ? 'Mois' : 'Month'}
                    </label>
                    <select
                      className="form-select form-select-lg text-center"
                      value={month}
                      onChange={(e) => handleMonthChange(e.target.value)}
                      required
                      style={{ fontSize: '1.1rem' }}
                    >
                      <option value="">--</option>
                      {months.map((m, i) => (
                        <option key={i + 1} value={String(i + 1)}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Année */}
                  <div className="col-4">
                    <label className="form-label small text-muted mb-1 d-block text-center fw-bold" style={{ color: 'var(--bs-primary)' }}>
                      {language === 'fr' ? '↓ Année ↓' : '↓ Year ↓'}
                    </label>
                    <select
                      className="form-select form-select-lg text-center"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      required
                      style={{ fontSize: '1.1rem', borderColor: 'var(--bs-primary)', borderWidth: 2 }}
                    >
                      <option value="">----</option>
                      {years.map((y) => (
                        <option key={y} value={String(y)}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Indice visuel sur mobile pour l'année */}
                {!year && (
                  <div className="mt-2 text-center">
                    <small className="text-warning">
                      {language === 'fr'
                        ? '⬆ Sélectionnez votre année de naissance dans la liste'
                        : '⬆ Select your birth year from the list'}
                    </small>
                  </div>
                )}

                {/* Aperçu de la date sélectionnée */}
                {isComplete && (
                  <div className="mt-2 p-2 rounded text-center" style={{ backgroundColor: 'rgba(var(--bs-success-rgb), 0.1)' }}>
                    <small className="text-success fw-semibold">
                      ✓{' '}
                      {language === 'fr'
                        ? `${day} ${months[parseInt(month) - 1]} ${year}`
                        : `${months[parseInt(month) - 1]} ${day}, ${year}`}
                    </small>
                  </div>
                )}
              </div>

              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={loading || !isComplete}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {translate('submitting', language)}...
                    </>
                  ) : (
                    translate('submit', language)
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
